import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Search, Users, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { genderMasterService } from '../../services';

const GenderMaster = () => {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const response = await genderMasterService.getAll({ search: searchTerm });
      setItems(response.data || []);
    } catch (error) {
      console.error('Error loading genders:', error);
      toast.error('Failed to load genders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => loadItems(), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
    if (item) {
      reset({ gender: item.gender, description: item.description, status: item.status });
    } else {
      reset({ gender: '', description: '', status: 1 });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (editingItem) {
        await genderMasterService.update(editingItem.id, data);
        toast.success('Gender updated successfully!');
      } else {
        const lastOrder = items.length > 0 ? items[items.length - 1].order : -1;
        data.order = lastOrder + 1;
        await genderMasterService.create(data);
        toast.success('Gender created successfully!');
      }
      closeModal();
      loadItems();
    } catch (error) {
      console.error('Error saving gender:', error);
      toast.error('Failed to save gender');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (item) => {
    try {
      setLoading(true);
      await genderMasterService.toggleStatus(item.id);
      toast.success('Gender status updated!');
      loadItems();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    
    if (draggedIndex !== index) {
      const newItems = [...items];
      const draggedItem = newItems[draggedIndex];
      newItems.splice(draggedIndex, 1);
      newItems.splice(index, 0, draggedItem);
      setItems(newItems);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = async () => {
    setDraggedIndex(null);
    if (isReordering) return;
    
    try {
      setIsReordering(true);
      const orders = items.map((item, index) => ({
        id: item.id,
        order: index
      }));
      
      await genderMasterService.reorder(orders);
      toast.success('Gender order updated!');
      loadItems();
    } catch (error) {
      console.error('Error reordering genders:', error);
      toast.error('Failed to update order');
      loadItems();
    } finally {
      setIsReordering(false);
    }
  };

  const moveUp = async (index) => {
    if (index === 0) return;
    const newItems = [...items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setItems(newItems);
    
    try {
      const orders = newItems.map((item, idx) => ({
        id: item.id,
        order: idx
      }));
      await genderMasterService.reorder(orders);
      toast.success('Gender moved up!');
    } catch (error) {
      console.error('Error moving gender:', error);
      toast.error('Failed to move gender');
      loadItems();
    }
  };

  const moveDown = async (index) => {
    if (index === items.length - 1) return;
    const newItems = [...items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setItems(newItems);
    
    try {
      const orders = newItems.map((item, idx) => ({
        id: item.id,
        order: idx
      }));
      await genderMasterService.reorder(orders);
      toast.success('Gender moved down!');
    } catch (error) {
      console.error('Error moving gender:', error);
      toast.error('Failed to move gender');
      loadItems();
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>Gender Master</h1>
          <button onClick={() => openModal()} className="btn btn-primary">
            <Plus size={18} />
            Add Gender
          </button>
        </div>

        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          <input type="text" placeholder="Search genders..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-box" style={{ paddingLeft: '40px' }} />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>Order</th>
                <th>ID</th>
                <th>Gender</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Updated Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr 
                  key={item.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  style={{ 
                    cursor: 'move',
                    opacity: draggedIndex === index ? 0.5 : 1,
                    backgroundColor: draggedIndex === index ? '#e5e7eb' : 'transparent'
                  }}
                >
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <GripVertical size={16} color="#6b7280" />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <button 
                          onClick={() => moveUp(index)}
                          disabled={index === 0}
                          style={{ 
                            border: 'none', 
                            background: 'none', 
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            opacity: index === 0 ? 0.3 : 1,
                            padding: '0'
                          }}
                          title="Move up"
                        >
                          <ArrowUp size={12} color={index === 0 ? "#9ca3af" : "#6b7280"} />
                        </button>
                        <button 
                          onClick={() => moveDown(index)}
                          disabled={index === items.length - 1}
                          style={{ 
                            border: 'none', 
                            background: 'none', 
                            cursor: index === items.length - 1 ? 'not-allowed' : 'pointer',
                            opacity: index === items.length - 1 ? 0.3 : 1,
                            padding: '0'
                          }}
                          title="Move down"
                        >
                          <ArrowDown size={12} color={index === items.length - 1 ? "#9ca3af" : "#6b7280"} />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td>{item.id}</td>
                  <td style={{ fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Users size={16} color="#6b7280" />
                      {item.gender}
                    </div>
                  </td>
                  <td>
                    <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {item.description || '-'}
                    </div>
                  </td>
                  <td>
                    <button
                      onClick={() => toggleStatus(item)}
                      className={`status-badge ${item.status === 1 ? 'status-active' : 'status-inactive'}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                      disabled={loading}
                    >
                      {item.status === 1 ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>{new Date(item.created_at).toLocaleDateString()}</td>
                  <td>{new Date(item.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => openModal(item)} className="btn btn-secondary" style={{ padding: '6px 8px', fontSize: '12px' }}>
                        <Edit size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading genders...</div>
        )}

        {!loading && items.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No genders found.</div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingItem ? 'Edit Gender' : 'Add New Gender'}</h2>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Gender</label>
                <input type="text" className="form-input" placeholder="Enter gender" {...register('gender', { required: 'Gender is required', minLength: { value: 2, message: 'Gender must be at least 2 characters' } })} />
                {errors.gender && (<div className="error-message">{errors.gender.message}</div>)}
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-input" placeholder="Enter description (optional)" rows={4} {...register('description')} />
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" {...register('status', { required: 'Status is required' })}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
                {errors.status && (<div className="error-message">{errors.status.message}</div>)}
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button type="button" onClick={closeModal} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : editingItem ? 'Update Gender' : 'Create Gender'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenderMaster;

